import { createContext, PropsWithChildren, ReactNode, useCallback, useContext, useEffect, useId, useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'

// Portalled descendants keep the entire chain open while the pointer crosses cards.
const HoverCardContext = createContext<{
  enter: () => void
  leave: () => void
  depth: number
  registerChild: (isActive: () => boolean) => () => void
} | null>(null)

type Props = PropsWithChildren<{
  content: ReactNode
  label: string
  width?: number
  onOpen?: () => void
}>

export const HoverCard = ({ children, content, label, width = 340, onOpen }: Props) => {
  const parent = useContext(HoverCardContext)
  const [visible, setVisible] = useState(false)
  const [position, setPosition] = useState({ top: 0, left: 0 })
  const trigger = useRef<HTMLSpanElement>(null)
  const card = useRef<HTMLDivElement>(null)
  const timer = useRef<ReturnType<typeof setTimeout>>()
  const suppressFocus = useRef(false)
  const descendants = useRef(new Set<() => boolean>())
  const id = useId()
  const depth = (parent?.depth ?? -1) + 1

  const isActive = useCallback((): boolean => {
    const activeElement = document.activeElement
    return !!(trigger.current?.matches(':hover') || card.current?.matches(':hover')
      || (activeElement && (trigger.current?.contains(activeElement) || card.current?.contains(activeElement)))
      || [...descendants.current].some(child => child()))
  }, [])
  const registerChild = useCallback((child: () => boolean) => {
    descendants.current.add(child)
    return () => { descendants.current.delete(child) }
  }, [])
  const registerWithParent = parent?.registerChild
  useEffect(() => registerWithParent?.(isActive), [registerWithParent, isActive])

  const enter = () => {
    clearTimeout(timer.current)
    parent?.enter()
    if (!visible) {
      setVisible(true)
      onOpen?.()
    }
  }
  const leave = () => {
    clearTimeout(timer.current)
    timer.current = setTimeout(() => { if (!isActive()) setVisible(false) }, 200)
    parent?.leave()
  }

  const updatePosition = useCallback(() => {
    if (!trigger.current || !card.current) return
    const rect = trigger.current.getBoundingClientRect()
    const { offsetHeight: height, offsetWidth: cardWidth } = card.current
    const margin = 8
    // Nested cards open beside their trigger so they don't cover the parent details.
    const parentRect = trigger.current.closest('[data-hover-card-panel]')?.getBoundingClientRect()
    let left = parentRect ? parentRect.right + margin : rect.left
    if (left + cardWidth > window.innerWidth - margin) {
      left = parentRect ? parentRect.left - cardWidth - margin : window.innerWidth - cardWidth - margin
    }
    let top = depth > 0 ? rect.top : rect.bottom + margin
    if (top + height > window.innerHeight - margin) top = rect.top - height - margin
    setPosition({
      left: Math.max(margin, left),
      top: Math.max(margin, Math.min(top, window.innerHeight - height - margin))
    })
  }, [depth])

  useLayoutEffect(() => {
    if (!visible) return
    updatePosition()
    const observer = new ResizeObserver(updatePosition)
    if (card.current) observer.observe(card.current)
    window.addEventListener('scroll', updatePosition, true)
    window.addEventListener('resize', updatePosition)
    return () => {
      observer.disconnect()
      window.removeEventListener('scroll', updatePosition, true)
      window.removeEventListener('resize', updatePosition)
    }
  }, [visible, updatePosition])

  useEffect(() => () => clearTimeout(timer.current), [])

  return <HoverCardContext.Provider value={{ enter, leave, depth, registerChild }}>
    <span ref={trigger} className="d-inline-block" tabIndex={0}
      aria-expanded={visible} aria-controls={visible ? id : undefined}
      onMouseOver={enter} onMouseOut={leave} onFocus={() => { if (!suppressFocus.current) enter() }} onBlur={leave}
      onKeyDown={event => {
        if (event.key === 'Tab' && !event.shiftKey && visible && trigger.current?.contains(event.target as Node)) {
          const firstLink = card.current?.querySelector<HTMLElement>('a[href], button, [tabindex="0"]')
          if (firstLink) {
            event.preventDefault()
            firstLink.focus()
          }
        }
        if (event.key === 'Escape' && visible) {
          event.stopPropagation()
          clearTimeout(timer.current)
          setVisible(false)
          suppressFocus.current = true
          trigger.current?.focus()
          suppressFocus.current = false
        }
      }}>
      {children}
      {visible && createPortal(<div ref={card} id={id} data-hover-card-panel role="region" aria-label={label}
        onMouseOver={enter} onMouseOut={leave} onFocus={enter} onBlur={leave}
        className="card shadow-lg border-0"
        style={{ position: 'fixed', ...position, zIndex: 10003 + depth, width,
          maxWidth: 'calc(100vw - 16px)', maxHeight: 'calc(100vh - 16px)', overflowY: 'auto',
          borderRadius: 12, whiteSpace: 'normal', textAlign: 'left', fontWeight: 'normal' }}>
        <div className="card-body p-4">{content}</div>
      </div>, document.body)}
    </span>
  </HoverCardContext.Provider>
}
